import { Button, cn, HeartHandIcon } from "@babybjorn/ui";
import type { TypedMoney } from "@commercetools/platform-sdk";
import { useTranslation } from "gatsby-plugin-react-i18next";
import type { FC, HTMLAttributes, ReactNode } from "react";
import { moneyToNumber } from "../../../../utils/commercetools/money";
import AddToCartButton from "../../components/AddToCartButton";
import { BazaarWidget } from "../../components/BazaarVoiceWidget/BazaarVoiceWidget";
import { useAppState } from "../../context/app/context";
import { useHasBazaarVoice } from "../../hooks/use-bazaarvoice";
import type { buttonProps } from "../../templates/product-page/product-page.component";
import { formatMoney } from "../../utils/cart/currency";
import Divider from "../general/Divider";
import Icon from "../general/Icon";
import Link from "../general/Link";

type PriceModuleProps = HTMLAttributes<HTMLElement> & {
	productName: string;
	productOptions: ReactNode;
	rawPrice: TypedMoney;
	inStock?: boolean;
	shippingInfo?: string;
	deliveryInfo?: string;
	giftwrapInfo?: string;
	warrantyPeriod?: string;
	warrantyLink?: string;
	addToCartLabel: string;
	stockLabel?: string;
	waitListLabel?: string;
	waitListForm?: ReactNode;
	waitListToggleInput?: () => void;
	waitListShowInput?: boolean;
	waitListDisabled?: boolean;
	addToCart: () => Promise<void>;
	sku: string;
	buttons?: buttonProps;
	babyListButton?: ReactNode;
	myRegistryButton?: ReactNode;
	fewInStock?: boolean;
};

const PriceModule: FC<PriceModuleProps> = ({
	productName,
	productOptions,
	rawPrice,
	inStock,
	shippingInfo,
	deliveryInfo,
	giftwrapInfo,
	warrantyPeriod,
	warrantyLink,
	addToCartLabel,
	stockLabel,
	waitListLabel,
	waitListForm,
	waitListToggleInput,
	waitListShowInput,
	waitListDisabled,
	addToCart,
	sku,
	buttons,
	babyListButton,
	myRegistryButton,
	fewInStock,
	...rest
}) => {
	const { t } = useTranslation();
	const numberOfButtons =
		(buttons ? buttons.length : 0) +
		(babyListButton ? 1 : 0) +
		(myRegistryButton ? 1 : 0);

	const hasCredits = shippingInfo || deliveryInfo || giftwrapInfo;
	const hasBazaarVoice = useHasBazaarVoice();
	const COUNTRIES_WITH_TAX_NOTICE = new Set(["NO", "CH"] as const);
	const { userCountry } = useAppState();
	return (
		<section
			data-id="price-module"
			className={cn(
				"@container max-md:-mt-6",
				"flex flex-col max-md:-mx-[calc(var(--spacing-bb)+1%)]",
			)}
			{...rest}
		>
			<div
				className={cn(
					"flex h-fit w-full flex-col items-center gap-4 bg-white px-[var(--margin)] py-6",
					"md:px-4 lg:gap-6 lg:rounded-t-md lg:rounded-r-md lg:px-6 lg:py-6",
				)}
			>
				<header className="mb-4 flex flex-col items-center gap-y-1 text-center max-md:hidden">
					{hasBazaarVoice && (
						<a
							href="#reviews"
							className="flex min-h-[27.1px] gap-1 decoration-0"
						>
							<BazaarWidget type="inline_rating" productSku={sku} />
						</a>
					)}
					<h1 className="!bb-text-display-h2">{productName}</h1>
				</header>
				{productOptions}
				<Divider variant="horizontal" />

				<div className="flex w-full flex-col items-center gap-y-3">
					<div
						itemScope
						itemProp="offers"
						itemType="https://schema.org/Offer"
						className="text-gray-4 flex items-center gap-x-2"
					>
						<meta itemProp="priceCurrency" content={rawPrice.currencyCode} />
						<meta
							itemProp="price"
							content={moneyToNumber(rawPrice).toString()}
						/>

						<p
							className={cn("!bb-text-body !text-[16px] md:!text-[18px]", {
								"text-gray-2 line-through": !inStock,
							})}
						>
							{formatMoney(rawPrice)}
						</p>
						{(fewInStock || !inStock) && (
							<p
								className={cn("bb-text-body", {
									"text-gray-2": inStock,
									"text-error": !inStock,
								})}
							>
								{stockLabel}
							</p>
						)}
					</div>
					{inStock && (
						<>
							{COUNTRIES_WITH_TAX_NOTICE.has(
								(userCountry ?? "").toUpperCase() as "NO" | "CH",
							) && (
								<p className="!bb-text-micro my-1 w-full text-center">
									{t("Price excludes VAT.")}
								</p>
							)}
							<div className="w-full">
								<AddToCartButton
									label={addToCartLabel}
									icon="cart"
									fullWidth
									onClick={addToCart}
									buttonType="large"
								/>
							</div>
						</>
					)}
					{!inStock && !waitListDisabled && (
						<Button
							variant="outline"
							size="large"
							onClick={waitListToggleInput}
							className={cn("flex w-full flex-col items-center gap-y-3", {
								hidden: waitListShowInput,
							})}
						>
							{waitListLabel}
						</Button>
					)}
					{waitListShowInput && waitListForm}
				</div>
			</div>
			<div
				className={cn(
					"flex h-fit w-full flex-col gap-4 bg-white/50 px-[var(--margin)] py-4",
					"md:px-4 lg:gap-6 lg:px-6 lg:py-6",
				)}
			>
				<div className="flex flex-col justify-between gap-2">
					<div
						className={cn("flex w-full justify-between gap-1", {
							"justify-end": !hasCredits,
						})}
					>
						{hasCredits && (
							<div className="flex flex-col gap-1">
								{shippingInfo && (
									<span className="text-gray-2 flex gap-2">
										<Icon
											icon="shipping"
											color="var(--color-gray-2)"
											className="max-w-4 md:max-w-5"
										/>
										<p className="!bb-text-tiny">{shippingInfo}</p>
									</span>
								)}
								{deliveryInfo && (
									<span className="text-gray-2 flex gap-2">
										<Icon
											icon="calendar"
											color="var(--color-gray-2)"
											className="max-w-4 md:max-w-5"
										/>
										<p className="!bb-text-tiny">{deliveryInfo}</p>
									</span>
								)}
								{giftwrapInfo && (
									<span className="text-gray-2 flex gap-2">
										<Icon
											icon="gift"
											color="var(--color-gray-2)"
											className="max-w-4 md:max-w-5"
										/>
										<p className="!bb-text-tiny">{giftwrapInfo}</p>
									</span>
								)}
								{!!warrantyPeriod && (
									<span className="text-gray-2 flex items-center gap-2">
										<HeartHandIcon className="w-4 md:w-5" />
										<p className="!bb-text-tiny">
											{`${t("This comes with a")} `}
											<Link
												href={warrantyLink}
												className="!bb-text-tiny !underline"
											>
												{`${warrantyPeriod}-${t("year")} ${t("guarantee")}.`}
											</Link>
										</p>
									</span>
								)}
							</div>
						)}
					</div>
				</div>
				<div
					className={cn(
						{ hidden: numberOfButtons > 0 },
						"grid grid-cols-1 gap-x-4 gap-y-2 @[480px]:grid-cols-2",
						{
							"[&>*]:col-span-full":
								numberOfButtons === 1 || numberOfButtons % 2,
						},
					)}
				>
					{babyListButton}
					{myRegistryButton}
					{buttons?.map((button) => (
						<Button
							key={button.label}
							variant="outline"
							size="always-small"
							className="font-semibold"
							asChild
						>
							<Link href={button.href} target={button.target}>
								{button.label}
							</Link>
						</Button>
					))}
				</div>
			</div>
		</section>
	);
};

export default PriceModule;
